import type { InstructorSummaryDTO } from "@/modules/master/domain/contracts";
import { formatDate } from "@/shared/lib/date-time";
import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import {
  BookOpenIcon,
  CalendarIcon,
  CheckCircleIcon,
  DollarSignIcon,
  UsersIcon,
} from "lucide-react";

type InstructorSummaryCardsProps = {
  summary: InstructorSummaryDTO | undefined;
  isLoading?: boolean;
};

export function InstructorSummaryCards({
  summary,
  isLoading,
}: InstructorSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-3">
              <CardTitle className="font-normal text-muted-foreground text-sm">
                ...
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">...</p>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* ຊົ່ວໂມງຮຽນຄລາດສ໌ Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-normal text-muted-foreground text-sm">
                ຊົ່ວໂມງຮຽນຄລາດສ໌
              </CardTitle>
              <BookOpenIcon className="size-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-lg">
              {summary?.classSessions.total ?? 0}
            </p>
            <p className="mt-1 text-muted-foreground text-xs">
              {summary?.classSessions.completed ?? 0} ສຳເລັດ •{" "}
              {summary?.classSessions.scheduled ?? 0} ຈັດຕາຕະລາງ
            </p>
          </CardContent>
        </Card>

        {/* Private Lessons Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-normal text-muted-foreground text-sm">
                Private Lessons
              </CardTitle>
              <UsersIcon className="size-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-lg">
              {summary?.privateLessons.total ?? 0}
            </p>
            <p className="mt-1 text-muted-foreground text-xs">
              {summary?.privateLessons.completed ?? 0} ສຳເລັດ •{" "}
              {summary?.privateLessons.scheduled ?? 0} ຈັດຕາຕະລາງ
            </p>
          </CardContent>
        </Card>

        {/* Check-ins Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-normal text-muted-foreground text-sm">
                Check-ins
              </CardTitle>
              <CheckCircleIcon className="size-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-lg">
              {summary?.checkIns.total ?? 0}
            </p>
            <p className="mt-1 text-muted-foreground text-xs">
              {summary?.checkIns.classSessions ?? 0} Class •{" "}
              {summary?.checkIns.privateLessons ?? 0} Private
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-normal text-muted-foreground text-sm">
                ຄ່າຈ້າງຄາດຄະເນ
              </CardTitle>
              <DollarSignIcon className="size-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="font-semibold text-destructive text-lg">
              {(summary?.revenue ?? 0).toLocaleString()} ກີບ
            </p>
            <p className="mt-1 text-muted-foreground text-xs">
              ຈາກ ຊົ່ວໂມງຮຽນ ສຳເລັດ — ຕາມອັດຕາທີ່ຕັ້ງ (ຕໍ່ຊົ່ວໂມງ ຫຼື ຕໍ່ຄັ້ງ)
            </p>
          </CardContent>
        </Card>
      </div>

      {(summary?.lastClassSession || summary?.lastPrivateLesson) && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-normal text-muted-foreground text-sm">
                ວັນສອນຫຼ້າສຸດ
              </CardTitle>
              <CalendarIcon className="size-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              {summary.lastClassSession ? (
                <p>
                  <span className="text-muted-foreground">Class: </span>
                  <span className="font-semibold">
                    {formatDate(summary.lastClassSession)}
                  </span>
                </p>
              ) : null}
              {summary.lastPrivateLesson ? (
                <p>
                  <span className="text-muted-foreground">Private: </span>
                  <span className="font-semibold">
                    {formatDate(summary.lastPrivateLesson)}
                  </span>
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
